import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  NoteColors,
  NoteV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  SwitchV2,
  SwitchVarient,
  TextInputV2,
} from '@labrav/react-components'
import { faCircleInfo, faGift } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './ProductFreeTrial.styles'
import { Typography } from '@mui/material'
import {
  ContractDimension,
  FlyoutProductAddEditSectionName,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { FormikProps, useFormikContext } from 'formik'
import {
  eulaStandardContractUrl,
  freeTrialFlyoutProduct,
} from '../../../common/utils/constants'
import { EulaSectionContent } from '../../../common/components/EulaSection/EulaSection'
import { defaultNow } from '../../../common/utils/dateTimeNow'
import { EulaType } from '../../../types/privateOffer'
import { ProductFreeTrialDimensions } from '../ProductFreeTrialDimensions/ProductFreeTrialDimensions'
import { isEmpty } from 'lodash'

export const ProductFreeTrial = () => {
  const { classes } = useStyles()
  const formikProps = useFormikContext() as FormikProps<FlyoutProductSections>
  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleBlur,
    setFieldError,
    setFieldTouched,
  } = formikProps
  const onClickOfHelpFreeTrial = () => {
    const newWind = window.open(
      freeTrialFlyoutProduct,
      '_blank',
      'noopener noreferrer'
    )
    if (newWind) {
      newWind.opener = null
    }
  }

  const onChangeSwitch = (e: { target: { checked: boolean } }) => {
    const freeTrialObject = {
      isEnable: e.target.checked,
      dimensions: [],
      numberOfDays: null,
      eulaType:
        values?.[FlyoutProductAddEditSectionName.GENERAL_INFORMATION]?.eula,
      eulaFiles: [],
    }
    setFieldValue('productDimensions.freeTrial', freeTrialObject)
    setFieldTouched('productDimensions.freeTrial', false)
  }

  const isEnableFreeTrial =
    values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
      ?.isEnable

  const { freeTrial } =
    values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]
  const freeTrialTouched = touched?.productDimensions?.freeTrial
  const freeTrialErrors = errors?.productDimensions?.freeTrial
  const eulaButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: EulaType.PUBLIC,
      label: 'Public offer legal terms',
      type: RadioButtonType.STANDARD,
      ...(values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]
        ?.freeTrial?.eulaType === EulaType.STANDARD
        ? { url: eulaStandardContractUrl }
        : {}),
    },
    {
      key: EulaType.STANDARD,
      label: 'Standard contract for AWS marketplace (SCMP)',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
      url: eulaStandardContractUrl,
    },
    {
      key: EulaType.CUSTOM,
      label: 'Custom EULA',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Provide a URL to a custom end-user license agreement (EULA) for your product.',
    },
  ]

  const updateErr = (error: string) => {
    setFieldError('productDimensions.freeTrial.eulaFile', error)
  }

  const productDimensions =
    values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS].dimensions

  const uniqueProductDimensions = productDimensions.reduce(
    (unique: ContractDimension[], o: ContractDimension) => {
      if (isEmpty(o.label)) {
        return unique
      }

      if (!unique.some(obj => obj.label === o.label)) {
        unique.push(o)
      }
      return unique
    },
    []
  )

  return (
    <BoxV2
      heading={'Free trial'}
      isOptional
      headingIcon={
        <FontAwesomeIcon
          icon={faGift}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <ButtonV2
          key={'help-for-free-trial'}
          styleType="link"
          data-testid="help-button-free-trial"
          icon={
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ height: '16px', width: '16px', fontSize: '16px' }}
            />
          }
          onClick={onClickOfHelpFreeTrial}
        >
          Help & FAQs
        </ButtonV2>
      }
    >
      <div className={classes.root} data-testid={'enable-free-trial'}>
        <div className={classes.description}>
          Free trials are a way to provide buyers the option to evaluate your
          AWS Marketplace software as a service (SaaS) product before making a
          purchase. Offering free trials can help you increase the number of
          potential buyers who consider and purchase your product while
          evaluating their SaaS product options. After a buyer subscribes to
          your product, your product performs entitlement checks the same way it
          does for paid customers.
        </div>

        <NoteV2
          backgroundColor={NoteColors.YELLOW}
          description={
            "Free Trial will go live only once the listing is in the 'Public' status. Free Trial can be created even later from the platform."
          }
        />
        <div className={classes.enableSwitch}>
          <Typography className={classes.bannerDescription}>
            Would you like to enable a free trial for this listing?
          </Typography>
          <div className={classes.switchWrapper}>
            <Typography className={classes.bannerDescription}>
              Enable free trial
            </Typography>
            <SwitchV2
              checked={isEnableFreeTrial}
              onChange={onChangeSwitch}
              switchVarient={SwitchVarient.DARK}
              data-testid={'free-trial-switch'}
            />
          </div>
        </div>
        {isEnableFreeTrial && (
          <>
            <div>
              <ProductFreeTrialDimensions
                productDimensions={uniqueProductDimensions}
              />
            </div>
            <div>
              <Typography className={classes.heading}>
                Number of free trial days
              </Typography>
              <Typography className={classes.subValue}>
                This is the description of your plan that helps your buyers
                understand its offerings.
              </Typography>

              <div className={classes.numberOfDays}>
                <TextInputV2
                  keyName="productDimensions.freeTrial.numberOfDays"
                  title={'Enter days'}
                  supportingText="Should be between 7-90 days."
                  type="number"
                  placeHolder=""
                  size={'medium'}
                  value={freeTrial?.numberOfDays || ''}
                  updatedValue={freeTrial?.numberOfDays || ''}
                  onBlur={handleBlur}
                  onChange={e =>
                    formikProps.setFieldValue(
                      'productDimensions.freeTrial.numberOfDays',
                      e?.target?.value
                    )
                  }
                  isError={
                    !!(
                      freeTrialErrors?.numberOfDays &&
                      freeTrialTouched?.numberOfDays
                    )
                  }
                  errorText={
                    (!!freeTrialTouched?.numberOfDays &&
                      freeTrialErrors?.numberOfDays) ||
                    ''
                  }
                  isReadOnly={false}
                  isRequired={false}
                  showSpinner={false}
                />
              </div>
              <div className={classes.eulaWrapper}>
                <EulaSectionContent
                  eulaTypeKeyName={'productDimensions.freeTrial.eulaType'}
                  eulaFilesKeyName={'productDimensions.freeTrial.eulaFile'}
                  defaultNow={defaultNow}
                  eulaDescription={
                    <div className={classes.eulaDesc}>
                      At the point of purchase, buyers must agree to the terms
                      and conditions outlined in your product's end-user license
                      agreement (EULA).
                    </div>
                  }
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  isMultiFileUpload={true}
                  fileUploadDescription={
                    'Upload up to 5 files one by one, use plain text .txt or PDF formats only. The maximum file size is 10 MB.'
                  }
                  fileUploadButtonText={'Upload files'}
                  fileUploadContainerTitle={'Drag and drop files here'}
                  quantity={5}
                  eulaOptionsHeading={'Legal term documents'}
                  isOptional={freeTrial?.eulaType !== EulaType.CUSTOM}
                  eulaOptionSubHeading={
                    freeTrial?.eulaType === EulaType.STANDARD
                      ? 'Upload up to 5 files one by one to amend the Standard contract for AWS marketplace, use plain text .txt or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The filed will be presented to buyers in the order they are uploaded.'
                      : 'Upload up to 5 files one by one using plain text (.txt) or PDF formats only. The maximum file size is 10 MB. Once uploaded, all files will be combined into a single PDF. The files will be presented to buyers in the order they are uploaded'
                  }
                  showEulaFileUpload={true}
                  eulaOptions={eulaButtonOptionsV2}
                  fileSize={10}
                  updateErr={updateErr}
                  uploadError={
                    freeTrialTouched?.eulaFile ? freeTrialErrors?.eulaFile : ''
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    </BoxV2>
  )
}
